/** Font families served from our own origin (see app/assets/css/_fonts.scss). */
export const SELF_HOSTED_FONT_FAMILIES = ['IBM Plex Sans']

type BuilderCustomFont = { family?: unknown }

const isSelfHosted = (font: unknown): boolean =>
  SELF_HOSTED_FONT_FAMILIES.includes(
    String((font as BuilderCustomFont | null)?.family)
  )

/**
 * Returns Builder content with the self-hosted families removed from every `customFonts`
 * list, at any depth (the page and any embedded symbol content). Other fonts picked in
 * Builder are kept.
 *
 * The Builder SDK turns `data.customFonts` into `@font-face` rules that load each family
 * as static `.ttf` files from fonts.gstatic.com. For IBM Plex Sans those rules come after
 * our self-hosted variable font and win, so the page would download both.
 *
 * Only the path to each changed list is copied; untouched branches keep their original
 * reference, and input with nothing to remove is returned as is.
 */
export const withoutSelfHostedFonts = <T>(value: T): T => {
  if (Array.isArray(value)) {
    let copy: unknown[] | undefined
    value.forEach((item, index) => {
      const next = withoutSelfHostedFonts(item)
      if (next !== item) {
        copy ??= [...value]
        copy[index] = next
      }
    })
    return (copy ?? value) as T
  }

  if (value && typeof value === 'object') {
    let copy: Record<string, unknown> | undefined
    for (const [key, item] of Object.entries(value)) {
      if (key === 'customFonts' && Array.isArray(item)) {
        const kept = item.filter((font) => !isSelfHosted(font))
        if (kept.length !== item.length) {
          copy ??= { ...value }
          copy.customFonts = kept
        }
        continue
      }
      const next = withoutSelfHostedFonts(item)
      if (next !== item) {
        copy ??= { ...value }
        copy[key] = next
      }
    }
    return (copy ?? value) as T
  }

  return value
}
